/* Abbara — PDP JS islands.
 *
 * Each block is a self-contained IIFE wired to a data-attribute. None
 * of them depend on theme.js; they each guard their root element so
 * the script is safe to load on non-PDP pages.
 *
 * Islands:
 *   data-pdp-back        — referrer-aware "Back to <collection>" override
 *   data-pdp-gallery     — thumb-strip click swaps the main image
 *   data-sf-toggle       — size-finder open/close + cross-brand mapping
 *   data-pdp-form        — variant picker + live price/ATC update + qty
 *   data-pdp-color-label — variant-driven color caption + swatch sync
 */

(function () {
  'use strict';

  /* ── Back-link referrer override ──────────────────────────
     The server-rendered "Back to <X>" uses the collection currently
     associated with the product (Shopify-set collection context, or
     the product's first collection as fallback). But if the user
     genuinely arrived from a DIFFERENT collection or PLP filter URL,
     we want the back link to point THERE instead. Inspect the
     referrer; if it's a /collections/ URL on this origin, prefer it.
     The label stays as the server-rendered one (we can't fetch the
     collection title from the URL alone without an API call), unless
     the path matches our recorded collection handle. */
  (function () {
    const links = document.querySelectorAll('[data-pdp-back]');
    if (!links.length) return;
    let ref = document.referrer;
    if (!ref) return;
    let refUrl;
    try { refUrl = new URL(ref); } catch (_) { return; }
    if (refUrl.origin !== window.location.origin) return;
    // Only override for collection/search URLs — not for clicks
    // from the home page, cart, account, etc. (those wouldn't make
    // sense as a "back to shop" target).
    const path = refUrl.pathname;
    const isCollection = path.startsWith('/collections/') && !path.includes('/products/');
    const isSearch = path === '/search';
    if (!isCollection && !isSearch) return;
    // Build the override target (preserve query string for filtered
    // PLP views, e.g. ?filter.v.option.color=ink).
    const href = refUrl.pathname + refUrl.search;
    // Derive a reasonable label from the URL. For /collections/<h>
    // we use the handle prettified; for /search we use "Search".
    let label = '';
    if (isSearch) {
      label = refUrl.searchParams.get('q')
        ? 'Search results'
        : 'Search';
    } else {
      // /collections/<handle> or /collections/<handle>/...
      const handle = path.split('/')[2] || '';
      if (!handle || handle === 'all') {
        label = 'Shop';
      } else {
        label = handle.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
      }
    }
    links.forEach((a) => {
      a.setAttribute('href', href);
      const labelEl = a.querySelector('[data-pdp-back-label]');
      if (labelEl) labelEl.textContent = label;
    });
    // Mirror the override into the trail breadcrumb's collection link.
    const crumbLink = document.querySelector('[data-pdp-crumb-link]');
    if (crumbLink) {
      crumbLink.setAttribute('href', href);
      const crumbLabel = crumbLink.querySelector('[data-pdp-crumb-label]');
      if (crumbLabel) crumbLabel.textContent = label;
    }
  })();

  /* ── Gallery — thumb-strip swaps main image; cursor-follow zoom ── */
  document.querySelectorAll('[data-pdp-gallery]').forEach((gallery) => {
    const main = gallery.querySelector('.pdp__gallery-main');
    const mainImg = gallery.querySelector('[data-pdp-gallery-main-img]');
    const thumbs = gallery.querySelectorAll('[data-pdp-gallery-thumb]');
    if (!main || !mainImg) return;

    // Click/tap to zoom, then GRAB-DRAG to pan around the zoomed photo —
    // like a hand tool, on both mouse and touch. Uses Pointer Events so one
    // code path covers desktop dragging and mobile touch-hold-drag. Panning
    // is translate-based (the image moves under a fixed frame) and clamped to
    // the image edges. A tap that doesn't move toggles zoom in/out.
    const ZOOM = 2.4;
    let zoomed = false, dragging = false, moved = false;
    let startX = 0, startY = 0, tx = 0, ty = 0, baseTx = 0, baseTy = 0;

    const clampPan = () => {
      const r = main.getBoundingClientRect();
      // Max translate (in the image's own pre-scale units) before an edge
      // would pull inside the frame: half the overflow, divided by the scale.
      const maxX = (r.width  * (ZOOM - 1)) / 2 / ZOOM;
      const maxY = (r.height * (ZOOM - 1)) / 2 / ZOOM;
      tx = Math.max(-maxX, Math.min(maxX, tx));
      ty = Math.max(-maxY, Math.min(maxY, ty));
    };
    const applyPan = () => {
      mainImg.style.transform = zoomed ? 'scale(' + ZOOM + ') translate(' + tx + 'px,' + ty + 'px)' : '';
    };
    const setZoom = (on, px, py) => {
      zoomed = on;
      main.classList.toggle('is-zoomed', on);
      if (on) {
        // Centre the zoom on the point that was tapped/clicked.
        const r = main.getBoundingClientRect();
        tx = -((px - r.left) - r.width  / 2);
        ty = -((py - r.top)  - r.height / 2);
        clampPan();
      } else { tx = 0; ty = 0; }
      applyPan();
    };
    // Reset hook so the thumb-strip swap can drop the zoom cleanly.
    main._resetZoom = () => setZoom(false, 0, 0);

    // Kill the browser's native image drag so dragging pans instead of
    // starting a "drag image to another app" gesture on desktop.
    mainImg.setAttribute('draggable', 'false');
    main.addEventListener('dragstart', (e) => e.preventDefault());

    main.addEventListener('pointerdown', (e) => {
      startX = e.clientX; startY = e.clientY; moved = false;
      if (!zoomed) return;                 // not zoomed: a tap will zoom in on pointerup
      e.preventDefault();                  // block the native image-drag / text selection
      dragging = true;
      baseTx = tx; baseTy = ty;
      main.classList.add('is-dragging');
      try { main.setPointerCapture(e.pointerId); } catch (_) {}
    });
    main.addEventListener('pointermove', (e) => {
      if (!dragging) return;
      const dx = e.clientX - startX, dy = e.clientY - startY;
      if (Math.abs(dx) > 4 || Math.abs(dy) > 4) moved = true;
      tx = baseTx + dx / ZOOM;
      ty = baseTy + dy / ZOOM;
      clampPan();
      applyPan();
    });
    const endDrag = (e) => {
      if (!dragging) return;
      dragging = false;
      main.classList.remove('is-dragging');
      try { main.releasePointerCapture(e.pointerId); } catch (_) {}
    };
    main.addEventListener('pointerup', (e) => {
      const wasTap = Math.abs(e.clientX - startX) < 6 && Math.abs(e.clientY - startY) < 6 && !moved;
      endDrag(e);
      if (wasTap) setZoom(!zoomed, e.clientX, e.clientY);  // tap toggles zoom in / out
    });
    main.addEventListener('pointercancel', endDrag);

    // Thumb-strip swap.
    if (thumbs.length) {
      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          if (main._resetZoom) main._resetZoom(); else main.classList.remove('is-zoomed');
          const full = thumb.dataset.full;
          const zoom = thumb.dataset.zoom;
          if (!full || full === mainImg.getAttribute('src')) return;
          // Keep the current image visible while the next loads (no blank flash);
          // because all gallery images are preloaded, the swap is effectively instant.
          const next = new Image();
          next.onload = () => {
            mainImg.src = full;
            if (zoom) main.setAttribute('data-zoom-img', zoom);
          };
          next.src = full;
          thumbs.forEach((t) => t.setAttribute('aria-selected', t === thumb ? 'true' : 'false'));
        });
      });
    }

    // Preload every gallery image so colour/thumb swaps are INSTANT (served from
    // cache) instead of re-fetching each time. Runs on idle so it never blocks
    // first paint.
    const warmGallery = () => {
      thumbs.forEach((t) => { if (t.dataset.full) { const im = new Image(); im.src = t.dataset.full; } });
    };
    if ('requestIdleCallback' in window) requestIdleCallback(warmGallery, { timeout: 2500 });
    else setTimeout(warmGallery, 500);
  });

  /* ── Size buttons: render in a sensible order regardless of how the
     variant values were entered (XS, S, M, L, XL, XXL… then numeric sizes). ── */
  (function () {
    const RANK = { XXS: 0, XS: 1, S: 2, M: 3, L: 4, XL: 5, XXL: 6, '2XL': 6, XXXL: 7, '3XL': 7, '4XL': 8 };
    const rank = (label) => {
      const u = (label || '').trim().toUpperCase();
      if (u in RANK) return RANK[u];
      const n = parseFloat(u);
      return isNaN(n) ? 999 : 100 + n; // numeric sizes (30, 31…) sort after letters, ascending
    };
    document.querySelectorAll('.pdp__sizes, .pcard__sizes').forEach((box) => {
      const labels = Array.from(box.children);
      if (labels.length < 2) return;
      labels
        .map((el) => ({ el, r: rank((el.querySelector('.pdp__size') || el).textContent) }))
        .sort((a, b) => a.r - b.r)
        .forEach((x) => box.appendChild(x.el));
    });
  })();

  /* ── Size-finder ──────────────────────────────────────── */
  // Cross-fit size mapping comes from Theme Settings → Size finder.
  // Merchants enter a textarea, one brand per line:
  //     Brand Name | XS=XS, S=S, M=M, L=L, XL=XL, XXL=XXL
  // The merchant-set brand list is rendered into the <select> below
  // from main-product.liquid, which also reads from the same setting.
  // If the matrix is empty, the brand <select> + the recommendation
  // hide entirely (no placeholder garbage). Toggle labels are also
  // merchant-editable.
  const sfCfg = window.abbaraSizeFinder || {};
  const sfOpenLabel  = sfCfg.openLabel  || 'Find my size →';
  const sfCloseLabel = sfCfg.closeLabel || 'Close size finder';

  // Parse the matrix textarea into a JS object.
  const parseSizeMatrix = (raw) => {
    const out = {};
    if (!raw || typeof raw !== 'string') return out;
    raw.split('\n').forEach((line) => {
      const trimmed = line.trim();
      if (!trimmed) return;
      const [name, pairs] = trimmed.split('|').map((s) => s && s.trim());
      if (!name || !pairs) return;
      const mapping = {};
      pairs.split(',').forEach((pair) => {
        const [from, to] = pair.split('=').map((s) => s && s.trim());
        if (from && to) mapping[from.toUpperCase()] = to.toUpperCase();
      });
      if (Object.keys(mapping).length) out[name] = mapping;
    });
    return out;
  };
  const sizeMap = parseSizeMatrix(sfCfg.brandMatrix);
  const hasMatrix = Object.keys(sizeMap).length > 0;

  document.querySelectorAll('[data-sf-toggle]').forEach((btn) => {
    // If the matrix is empty, hide the toggle entirely.
    if (!hasMatrix) { btn.setAttribute('hidden', ''); return; }
    btn.textContent = sfOpenLabel;
    btn.addEventListener('click', () => {
      const panel = btn.closest('.pdp__opt-block')?.querySelector('[data-sf-panel]');
      if (!panel) return;
      const open = !panel.hidden;
      panel.hidden = open;
      btn.textContent = open ? sfOpenLabel : sfCloseLabel;
    });
  });
  document.querySelectorAll('[data-sf-panel]').forEach((panel) => {
    if (!hasMatrix) { panel.setAttribute('hidden', ''); return; }
    const brandSel = panel.querySelector('[data-sf-brand]');
    const sizeSel  = panel.querySelector('[data-sf-size]');
    const recEl    = panel.querySelector('[data-sf-rec]');
    const useBtn   = panel.querySelector('[data-sf-use]');

    // Populate the brand <select> from the merchant matrix (replaces
    // whatever placeholder options exist from main-product.liquid).
    if (brandSel) {
      brandSel.innerHTML = '';
      Object.keys(sizeMap).forEach((name) => {
        const opt = document.createElement('option');
        opt.value = name;
        opt.textContent = name;
        brandSel.appendChild(opt);
      });
    }

    const recalc = () => {
      if (!brandSel || !sizeSel) return;
      const brand = brandSel.value;
      const their = (sizeSel.value || '').toUpperCase();
      const rec = (sizeMap[brand] && sizeMap[brand][their]) || '—';
      if (recEl) recEl.textContent = rec;
      if (useBtn) useBtn.textContent = rec === '—' ? 'Use →' : 'Use ' + rec + ' →';
    };
    if (brandSel) brandSel.addEventListener('change', recalc);
    if (sizeSel)  sizeSel.addEventListener('change', recalc);
    if (useBtn) {
      useBtn.addEventListener('click', () => {
        const rec = (recEl && recEl.textContent || '').trim();
        if (!rec || rec === '—') return;
        document.querySelectorAll('input[name^="options[Size"]').forEach((r) => {
          if (r.value === rec) {
            r.checked = true;
            r.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
        panel.hidden = true;
        const toggle = panel.parentElement.querySelector('[data-sf-toggle]');
        if (toggle) toggle.textContent = sfOpenLabel;
      });
    }
    recalc();
  });

  /* ── Size-chart modal — open/close (mirrors the size-finder + quickview) ── */
  (function () {
    const modal = document.querySelector('[data-sizechart]');
    if (!modal) return;
    const panel = modal.querySelector('.sizechart__panel');
    let lastFocus = null;
    const open = () => {
      lastFocus = document.activeElement;
      modal.dataset.open = 'true';
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      if (panel) panel.focus();
    };
    const close = () => {
      if (modal.dataset.open !== 'true') return;
      modal.dataset.open = 'false';
      modal.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (lastFocus && lastFocus.focus) lastFocus.focus();
    };
    document.querySelectorAll('[data-sizechart-toggle]').forEach((b) => b.addEventListener('click', open));
    modal.addEventListener('click', (e) => { if (e.target.closest('[data-sizechart-close]')) close(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') close(); });
  })();

  /* Worn-by carousel island removed in the PDP redesign — the
     worn-by section was dropped from product.liquid in favour of a
     description + accordion block. Metafield data remains in
     Shopify admin in case the carousel is brought back later. */

  /* ── Variant picker — live price, ATC, color caption ── */
  document.querySelectorAll('[data-pdp-form]').forEach((form) => {
    const idInput     = form.querySelector('[data-pdp-variant-id]');
    const priceEl     = document.querySelector('[data-pdp-price]');
    const totalEl     = form.querySelector('[data-pdp-atc-total]');
    const qtyInput    = form.querySelector('[data-qty-input]');
    const colorScript = document.querySelector('[data-pdp-color-label]');
    const colorName   = document.querySelector('[data-pdp-color-name]');
    // Gallery refs for the colour→photo filter.
    const gallery     = document.querySelector('[data-pdp-gallery]');
    const galThumbs   = gallery ? Array.from(gallery.querySelectorAll('[data-pdp-gallery-thumb]')) : [];
    const galMain     = gallery ? gallery.querySelector('.pdp__gallery-main') : null;
    const galMainImg  = gallery ? gallery.querySelector('[data-pdp-gallery-main-img]') : null;
    const dec = form.querySelector('[data-qty-down]');
    const inc = form.querySelector('[data-qty-up]');
    if (dec && qtyInput) dec.addEventListener('click', () => {
      qtyInput.value = Math.max(1, parseInt(qtyInput.value || '1', 10) - 1);
      updateTotal();
    });
    if (inc && qtyInput) inc.addEventListener('click', () => {
      qtyInput.value = parseInt(qtyInput.value || '1', 10) + 1;
      updateTotal();
    });
    if (qtyInput) qtyInput.addEventListener('input', updateTotal);

    // Variants data loaded from a hidden JSON blob below.
    let variants = [];
    const dataEl = document.querySelector('#pdp-variants-data');
    if (dataEl) {
      try { variants = JSON.parse(dataEl.textContent); } catch (_) {}
    }
    function currentSelections() {
      const out = {};
      form.querySelectorAll('input[name^="options["]:checked').forEach((r) => {
        const name = r.name.match(/options\[(.*)\]/)?.[1];
        if (name) out[name] = r.value;
      });
      return out;
    }
    function findVariant(sel) {
      if (!variants.length) return null;
      return variants.find((v) => {
        return Object.keys(sel).every((k) => v.options[k] === sel[k]);
      });
    }
    function formatMoney(cents) {
      if (window.abbaraMoney) return window.abbaraMoney.format(cents);
      const cur = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'AED';
      return cur + ' ' + (cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 });
    }
    /* Colour → photos: show only thumbs whose data-color (downcased image alt
       text) contains the picked colour. If NOTHING matches (images aren't
       tagged by colour), show every thumb — untagged products work unchanged.
       Points the main image at the first visible thumb. */
    function filterGalleryByColor(colorVal) {
      if (galMain) galMain.classList.remove('is-zoomed'); // a colour change resets zoom
      if (!galThumbs.length) return;
      const want = (colorVal || '').trim().toLowerCase();
      const matches = want ? galThumbs.filter((t) => (t.dataset.color || '').includes(want)) : [];
      const showAll = !want || matches.length === 0;
      const visible = showAll ? galThumbs : matches;
      galThumbs.forEach((t) => { t.hidden = !visible.includes(t); });
      const first = visible[0];
      if (!first || !galMainImg) return;
      const full = first.dataset.full;
      const curSrc = galMainImg.getAttribute('src');
      const stillVisible = visible.some((t) => t.dataset.full === curSrc);
      if (full && !stillVisible && full !== curSrc) {
        // Keep the current image until the new one is ready (no blank flash).
        const pre = new Image();
        pre.onload = () => {
          galMainImg.src = full;
          if (first.dataset.zoom && galMain) galMain.setAttribute('data-zoom-img', first.dataset.zoom);
        };
        pre.src = full;
        galThumbs.forEach((t) => t.setAttribute('aria-selected', t === first ? 'true' : 'false'));
      }
    }
    /* Cross out sizes that aren't in stock for the currently-selected
       colour. For each Size radio, test (current colour + that size): if no
       available variant matches, mark its label .is-unavailable. Re-runs on
       every change, so switching colour re-evaluates the whole size run.
       Only Size labels are marked — colours are never crossed (a colour can
       still be valid in a different size). */
    function updateOptionAvailability() {
      if (!variants.length) return;
      const sel = currentSelections();
      form.querySelectorAll('input[name^="options["]').forEach((input) => {
        const m = input.name.match(/options\[(.*)\]/);
        if (!m) return;
        const trial = Object.assign({}, sel, { [m[1]]: input.value });
        const ok = variants.some((v) => v.available &&
          Object.keys(trial).every((k) => v.options[k] === trial[k]));
        const label = input.closest('.pdp__size-label');
        if (label) label.classList.toggle('is-unavailable', !ok);
      });
    }
    function updateTotal() {
      const sel = currentSelections();
      const v = findVariant(sel);
      const qty = parseInt(qtyInput?.value || '1', 10);
      if (v) {
        if (priceEl) priceEl.textContent = formatMoney(v.price);
        if (idInput) idInput.value = v.id;

        // Reflect availability on the add-to-cart button: enable + show
        // "label — total" when in stock, disable + show the sold-out label
        // when not. Rebuilding the innerHTML keeps the price total fresh.
        const atcBtn = form.querySelector('[data-pdp-atc]');
        if (atcBtn) {
          if (v.available) {
            atcBtn.removeAttribute('disabled');
            const lbl = atcBtn.dataset.atcLabel || 'Add to bag';
            // Label only — no price. The unit price is shown above the button,
            // so repeating it here was redundant.
            atcBtn.textContent = lbl;
          } else {
            atcBtn.setAttribute('disabled', '');
            atcBtn.textContent = atcBtn.dataset.soldoutLabel || 'Sold out';
          }
        } else if (totalEl) {
          totalEl.textContent = formatMoney(v.price * qty);
        }

        // Express "Buy it now" checkout: only shown for in-stock variants.
        const dynCheckout = document.querySelector('[data-pdp-dynamic-checkout]');
        if (dynCheckout) dynCheckout.hidden = !v.available;

        updateStockCounter(v);
      }
      // Color script caption follows the Color option selection.
      const colorVal = sel['Color'] || sel['Colour'];
      if (colorVal) {
        if (colorScript) colorScript.textContent = colorVal;
        if (colorName)   colorName.textContent   = colorVal;
      }
      // Filter the gallery to only the selected colour's photos.
      filterGalleryByColor(colorVal);
      // Cross out sizes that are sold out in the selected colour.
      updateOptionAvailability();
      // Sticky bar: show the selected options (e.g. "Midnight Navy · M").
      const stickyVarLabel = document.querySelector('[data-sticky-variant-label]');
      if (stickyVarLabel) {
        const parts = [];
        form.querySelectorAll('input[name^="options["]:checked').forEach((r) => parts.push(r.value));
        if (parts.length) stickyVarLabel.textContent = parts.join(' · ');
      }
    }

    /* Stock counter — refresh on variant change. Reads thresholds and
       templates from the data-* attributes set by the Liquid block, so
       merchant copy in the editor stays the source of truth. */
    function updateStockCounter(v) {
      const root = document.querySelector('[data-pdp-stock]');
      if (!root) return;
      if (!v || v.inventory_management == null || v.inventory_management === '') {
        root.innerHTML = '';
        return;
      }
      const qty = typeof v.inventory_quantity === 'number' ? v.inventory_quantity : 0;
      const threshold = parseInt(root.dataset.threshold || '5', 10);
      const showAlways = root.dataset.showAlways === 'true';
      const lowTpl = root.dataset.lowText || 'Only [count] left';
      const inTpl  = root.dataset.inText  || 'In stock and ready to ship';
      const outTpl = root.dataset.outText || 'Currently out of stock';
      root.dataset.qty = String(qty);
      let html = '';
      if (v.available === false) {
        html = '<span class="pdp__stock pdp__stock--out">' + outTpl + '</span>';
      } else if (qty <= threshold) {
        html = '<span class="pdp__stock pdp__stock--low">' + lowTpl.replace('[count]', String(qty)) + '</span>';
      } else if (showAlways) {
        html = '<span class="pdp__stock pdp__stock--in">' + inTpl + '</span>';
      }
      root.innerHTML = html;
    }
    form.addEventListener('change', updateTotal);
    updateTotal();
  });

  /* ── Sticky add-to-cart bar ───────────────────────────────
     A slim bar that slides up from the bottom once the buy
     column has scrolled out of view. Stays in lock-step with the
     main buy column: variant id + price mirror whatever the
     visitor has selected above. Submitting posts to /cart/add.js
     and refreshes the drawer via window.refreshCart() if present. */
  (function () {
    const bar    = document.querySelector('[data-pdp-sticky-atc]');
    const buyCol = document.querySelector('[data-pdp-form]');
    if (!bar || !buyCol) return;

    const stickyForm    = bar.querySelector('[data-sticky-atc-form]');
    const stickyVariant = bar.querySelector('[data-sticky-variant-id]');
    const stickyPrice   = bar.querySelector('[data-sticky-price]');
    const stickyBtn     = bar.querySelector('[data-sticky-atc-btn]');
    const mainVariant   = buyCol.querySelector('[data-pdp-variant-id]');
    const mainPrice     = document.querySelector('[data-pdp-price]');
    const mainAtc       = buyCol.querySelector('[data-pdp-atc]');

    // Keep the sticky bar's thumbnail in sync with the displayed gallery image,
    // so it always shows the selected colour (the gallery main image already
    // swaps on colour change; we just mirror it here).
    const stickyThumb = bar.querySelector('.pdp__sticky-atc-thumb');
    const galMainImg  = document.querySelector('[data-pdp-gallery-main-img]');
    if (stickyThumb && galMainImg) {
      const mirrorThumb = () => {
        const s = galMainImg.currentSrc || galMainImg.getAttribute('src');
        if (s && s !== stickyThumb.getAttribute('src')) stickyThumb.setAttribute('src', s);
      };
      mirrorThumb();
      if ('MutationObserver' in window) {
        new MutationObserver(mirrorThumb).observe(galMainImg, { attributes: true, attributeFilter: ['src', 'srcset'] });
      }
    }

    // Visibility — the bar appears whenever the actual Add-to-bag / Buy-it-now
    // buttons leave the viewport, on EVERY breakpoint (desktop + mobile). We
    // observe the buy_buttons block specifically — NOT the whole buy column —
    // so the bar shows the moment the buttons scroll away, even while other
    // parts of the column are still visible. The -72px bottom margin treats
    // "tucked behind the fixed bar" as off-screen too, so the bar never sits
    // on top of the live buttons. Falls back to the column if the hook is absent.
    const buyBox = document.querySelector('[data-pdp-buybox]') || buyCol;
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          const offscreen = !entry.isIntersecting;
          if (offscreen) {
            bar.removeAttribute('hidden');
            // Defer the data-visible flip so the [hidden] removal can
            // settle first — lets the slide-up transition actually play.
            requestAnimationFrame(() => {
              bar.setAttribute('data-visible', 'true');
              // Flag the body + expose the bar height so the back-to-top button
              // can lift above the sticky bar instead of overlapping it.
              document.body.style.setProperty('--sticky-atc-h', bar.offsetHeight + 'px');
              document.body.classList.add('pdp-sticky-on');
            });
          } else {
            bar.setAttribute('data-visible', 'false');
            document.body.classList.remove('pdp-sticky-on');
            // Wait for the slide-down transition before re-hiding.
            setTimeout(() => {
              if (bar.getAttribute('data-visible') === 'false') bar.setAttribute('hidden', '');
            }, 260);
          }
        });
      }, { rootMargin: '0px 0px -72px 0px', threshold: 0 });
      io.observe(buyBox);
    }

    // Mirror variant + price from the main buy column. The variant
    // picker IIFE above updates [data-pdp-variant-id] + [data-pdp-price]
    // via a `change` event on the form — listen on the same form and
    // copy the values straight across.
    const syncFromMain = () => {
      if (mainVariant && stickyVariant) stickyVariant.value = mainVariant.value;
      if (mainPrice && stickyPrice)     stickyPrice.textContent = mainPrice.textContent;
      // Mirror the sold-out state: if the main ATC is disabled, disable the
      // sticky button too so a sold-out variant can't be added from the bar.
      if (stickyBtn && mainAtc) {
        stickyBtn.disabled = mainAtc.hasAttribute('disabled');
      }
    };
    buyCol.addEventListener('change', () => {
      // Defer one tick so the main variant IIFE has already updated.
      setTimeout(syncFromMain, 0);
    });
    syncFromMain();

    // Submit handler — POST to /cart/add.js, then nudge the drawer.
    // IMPORTANT: theme.js has a document-level submit listener that also
    // catches every form[action*="/cart/add"]. To avoid a double-add (one
    // POST from here, one from theme.js bubbling), we stopPropagation so
    // only this handler runs. We mirror theme.js's full behavior (refresh
    // drawer + open + toast) so the UX is identical to the main ATC.
    if (stickyForm) {
      stickyForm.addEventListener('submit', (e) => {
        e.preventDefault();
        e.stopPropagation();
        const btn = stickyForm.querySelector('button[type="submit"]');
        const original = btn ? btn.textContent : '';
        if (btn) { btn.disabled = true; btn.textContent = 'Adding…'; }
        // Instant toast on press so there's no silent gap before "Added".
        if (typeof window.abbaraToast === 'function') {
          try { window.abbaraToast('Adding ' + (stickyForm.dataset.productTitle || 'item') + ' to cart…'); } catch (_) {}
        }

        const fd = new FormData(stickyForm);
        fetch('/cart/add.js', {
          method: 'POST',
          body: fd,
          headers: { 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          credentials: 'same-origin'
        })
          .then(async (r) => {
            // A 422 (sold out / stock limit) also parses as JSON — checking
            // r.ok is what separates a real add from a refusal. Without it
            // the customer sees "Added" while the bag stays empty.
            if (!r.ok) {
              let msg = 'Could not add to bag — please try again';
              try {
                const j = await r.json();
                msg = j.description || j.message || msg;
              } catch (_) {}
              if (typeof window.abbaraToast === 'function') window.abbaraToast(msg);
              return;
            }
            // Refresh the count + toast, but keep the shopper on the page —
            // the drawer opens only when they tap the cart icon.
            if (typeof window.refreshCart === 'function') {
              try { await window.refreshCart(); } catch (_) {}
            }
            if (typeof window.abbaraToast === 'function') {
              const title = stickyForm.dataset.productTitle || 'item';
              try { window.abbaraToast('Added to cart · ' + title); } catch (_) {}
            }
          })
          .catch(() => {
            if (typeof window.abbaraToast === 'function') window.abbaraToast('Network error — please try again');
          })
          .finally(() => {
            if (btn) { btn.disabled = false; btn.textContent = original; }
          });
      });
    }
  })();
})();

/* ── Floating colour picker (mobile) ───────────────────────────────
   A small pill of colour dots that floats just above the sticky add-to-
   cart bar WHILE the gallery image is on screen — so a shopper can switch
   colours and watch the photo change without scrolling down to the real
   swatches and back up. Tapping a dot drives the real Color radio (which
   updates the image, price, and availability), marks the active dot, and
   briefly fades the colour's name in then out. It hides the moment the
   gallery scrolls out of view, leaving the add-to-bag bar on its own. */
(function floatingColorPicker() {
  const gallery = document.querySelector('[data-pdp-gallery]');
  const form    = document.querySelector('[data-pdp-form]');
  const bar     = document.querySelector('[data-pdp-sticky-atc]');
  if (!gallery || !form) return;

  const labels = Array.from(form.querySelectorAll('.pdp__swatches .pdp__swatch-label'));
  if (labels.length < 2) return; // nothing to switch between

  const wrap = document.createElement('div');
  wrap.className = 'pdp-floatcolor';
  wrap.setAttribute('aria-hidden', 'true');

  const name = document.createElement('span');
  name.className = 'pdp-floatcolor__name';
  name.setAttribute('aria-live', 'polite');

  const dots = document.createElement('div');
  dots.className = 'pdp-floatcolor__dots';

  const entries = labels.map((label) => {
    const input = label.querySelector('input');
    const sw    = label.querySelector('.pdp__swatch');
    const value = input ? input.value : '';
    const dot = document.createElement('button');
    dot.type = 'button';
    dot.className = 'pdp-floatcolor__dot';
    dot.dataset.value = value;
    dot.setAttribute('aria-label', value);
    if (sw && sw.getAttribute('style')) dot.setAttribute('style', sw.getAttribute('style'));
    dot.addEventListener('click', () => select(value, true));
    dots.appendChild(dot);
    return { value, input, dot };
  });

  wrap.appendChild(name);
  wrap.appendChild(dots);
  document.body.appendChild(wrap);

  let nameTimer = null;
  function flashName(value) {
    name.textContent = value;
    name.classList.add('is-show');
    clearTimeout(nameTimer);
    nameTimer = setTimeout(() => name.classList.remove('is-show'), 1300);
  }
  function markActive(value) {
    entries.forEach((e) => e.dot.classList.toggle('is-active', e.value === value));
  }
  function select(value, flash) {
    const e = entries.find((x) => x.value === value);
    if (!e || !e.input) return;
    if (!e.input.checked) {
      e.input.checked = true;
      e.input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    markActive(value);
    if (flash) flashName(value);
  }

  // Keep the active dot in sync when colour is changed from the real swatches.
  form.addEventListener('change', () => {
    const checked = form.querySelector('.pdp__swatches input:checked');
    if (checked) markActive(checked.value);
  });
  const initial = form.querySelector('.pdp__swatches input:checked');
  if (initial) markActive(initial.value);

  // Sit just above the sticky bar (measure it; fall back to ~76px while hidden).
  function positionAboveBar() {
    const h = (bar && bar.offsetHeight) ? bar.offsetHeight : 76;
    wrap.style.bottom = (h + 12) + 'px';
  }
  // Show only while the gallery image is in view.
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver((items) => {
      items.forEach((entry) => {
        if (entry.isIntersecting) {
          positionAboveBar();
          wrap.classList.add('is-visible');
          wrap.setAttribute('aria-hidden', 'false');
          // Re-measure once the bar has slid up.
          setTimeout(positionAboveBar, 340);
        } else {
          wrap.classList.remove('is-visible');
          wrap.setAttribute('aria-hidden', 'true');
        }
      });
    }, { threshold: 0, rootMargin: '0px 0px -90px 0px' });
    io.observe(gallery);
  }
  window.addEventListener('resize', positionAboveBar);
})();
